import { Briefcase, FileText } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import { getSavedViewMode, saveViewMode } from "@/utils/viewMode";
import { ViewToggle, ViewMode } from "@/components/common/ViewToggle";
import { useTranslation } from "react-i18next";

interface Match {
  id: string;
  candidateId: string;
  candidateName: string;
  jobId: string;
  jobTitle: string;
  score: number;
}

interface CompanyMatchesTabProps {
  matches: Match[];
  loading: boolean;
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  viewMode: 'list' | 'card';
  setViewMode: (value: 'list' | 'card') => void;
}

export const CompanyMatchesTab = ({
  matches,
  loading,
  searchTerm,
  setSearchTerm,
  viewMode,
  setViewMode
}: CompanyMatchesTabProps) => {
  const { t } = useTranslation();

  useEffect(() => {
    const savedViewMode = getSavedViewMode('company-matches', 'list');
    setViewMode(savedViewMode as 'list' | 'card');
  }, []);

  useEffect(() => {
    saveViewMode('company-matches', viewMode as any);
  }, [viewMode]);

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">{t("companies.detail.tabs.matches")}</h3>
          <div className="flex space-x-2">
            <input
              type="text"
              placeholder={t("companies.matches.searchPlaceholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="px-3 py-2 border rounded-md text-sm"
            />
            <ViewToggle 
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              label=""
            />
          </div>
        </div>
        
        {loading ? (
          <div className="animate-pulse space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-20 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        ) : matches.length > 0 ? (
          <div className="space-y-3">
            {matches.map((match) => (
              <div key={match.id} className="border rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <h4 className="font-medium">{match.candidateName} - {match.jobTitle}</h4>
                    <p className="text-sm text-gray-600">{t("companies.matches.score", { score: match.score })}</p>
                  </div>
                  <Link to={`/matches/${match.id}`}>
                    <Button variant="outline">{t("common.view")}</Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 border rounded-lg">
            <Briefcase className="h-8 w-8 text-gray-400 mx-auto" />
            <p className="mt-2 text-gray-500">{t("companies.matches.noMatches")}</p>
          </div>
        )}
      </div>
    </div>
  );
};
